<template>
  <div
    ref="prizePoolRef"
    class="prizePool"
    :class="{
      prizePoolMobile: $device.isMobile
    }"
  >
    <span v-if="title" class="prizePoolContainerTitle" :class="{ 'ru-locale': $i18n.locale === 'ru' }">{{
      title
    }}</span>

    <div class="prizePoolContainerWrapper">
      <div class="swiper-button-prev"></div>

      <div class="prizePoolContainer">
        <el-skeleton :loading="!token || isLoadingData" animated class="skeleton">
          <template #template>
            <el-skeleton-item
              class="skeleton-item"
              :style="[prizePoolRef && `height: ${prizePoolRef.clientHeight - 5}px`]"
            />
          </template>

          <template #default>
            <div v-if="tokensList && tokensList.length > 1" class="swiper-container">
              <swiper
                :modules="[Navigation, EffectCoverflow]"
                :navigation="{
                  nextEl: '.swiper-button-next',
                  prevEl: '.swiper-button-prev'
                }"
                :slide-shadows="false"
                :slides-per-view="1"
                :space-between="0"
                centered-slides
                effect="coverflow"
                :loop="false"
                class="token-swiper"
                @swiper="onSwiper"
                @slide-change="onSlideChange"
              >
                <swiper-slide
                  v-for="(tokenItem, index) in tokensList"
                  :key="tokenItem.id || index"
                  class="swiper-slide"
                >
                  <div class="token-slide-content">
                    <img :src="getTokenImage(tokenItem)" alt="" class="token-image" />
                    <span class="token-value">{{ value }}</span>
                  </div>
                </swiper-slide>
              </swiper>
            </div>
          </template>
        </el-skeleton>
      </div>
      <div class="swiper-button-next"></div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Tokens } from '~/utils/constants';
import tokens from 'public/img/tokens';
import type { IFlipCoinTokenConfig } from '~/types/apiService';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, EffectCoverflow } from 'swiper/modules';
import type { Swiper as SwiperType } from 'swiper';

// Swiper styles
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

const props = defineProps<{
  value: string | undefined;
  token?: Tokens;
  title?: string;
  isLoadingData?: boolean;
}>();

const emit = defineEmits<{
  'token-change': [token: Tokens];
}>();

const { blockchain } = useEnvs();
const prizePoolRef = ref();

const { data: tokensList } = useNuxtData<IFlipCoinTokenConfig[]>('tokensList');
const swiperInstance = ref<SwiperType>();

const onSwiper = (swiper: SwiperType) => {
  swiperInstance.value = swiper;

  const currentIndex = findCurrentTokenIndex();
  if (currentIndex >= 0) {
    swiper.slideTo(currentIndex);
  }
};

const onSlideChange = (swiper: SwiperType) => {
  const activeIndex = swiper.activeIndex;
  const newToken = tokensList.value?.[activeIndex];

  if (newToken) {
    const tokenType = tokenMetaData(blockchain.contracts, newToken.tokenAddress).name.contract as Tokens;
    emit('token-change', tokenType);
  }
};

const findCurrentTokenIndex = () => {
  if (!props.token || !tokensList.value?.length) return 0;

  const currentTokenAddress = blockchain.contracts[props.token].addresses.contract;
  const index = tokensList.value.findIndex((token) => token.tokenAddress === currentTokenAddress);
  return index >= 0 ? index : 0;
};

watch(
  [() => props.token, tokensList],
  () => {
    if (swiperInstance.value) {
      const currentIndex = findCurrentTokenIndex();
      if (currentIndex >= 0 && swiperInstance.value.activeIndex !== currentIndex) {
        swiperInstance.value.slideTo(currentIndex);
      }
    }
  },
  { immediate: true }
);

const getTokenImage = (tokenItem: IFlipCoinTokenConfig) => {
  const tokenMeta = tokenMetaData(blockchain.contracts, tokenItem.tokenAddress);
  const contract = tokenMeta.name.contract;
  return tokens[contract as Tokens].toString();
};

const token = computed(() => {
  if (!props?.token) {
    return null;
  }

  return tokenMetaData(blockchain.contracts, blockchain.contracts[props?.token].addresses.contract);
});
</script>

<style scoped lang="scss">
.prizePool {
  position: relative;
  display: flex;
  gap: 12px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

.prizePoolContainerTitle {
  font-family: Eczar;
  font-weight: 700;
  font-size: 28px;
  line-height: 1;
  color: var(--main-text-color);
}

.prizePoolMobile {
  .prizePoolContainerTitle.ru-locale {
    margin-bottom: 4px;
  }
}

.prizePoolContainerWrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.prizePoolContainer {
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  background-color: #070000;
  height: 60px;
  border-radius: 15px;
  border: solid 2.5px rgba(248, 169, 0, 1);
  width: 282px;
  box-sizing: border-box;
  overflow: hidden;
  position: relative;

  img {
    height: 50px;
    width: 50px;
  }

  .prizePoolValue {
    font-weight: 700;
    font-family: Eczar;
    font-size: 32px;
    height: 28px;
    line-height: 1;
    color: var(--main-text-color);
  }

  .prizePoolValue.ru-locale {
    height: 34px;
  }
}

.skeleton {
  max-width: calc(100% - 5px);
  height: 100%;
  top: 2.5px;
  left: 50%;
  transform: translateX(-50%);
  position: absolute;

  --el-skeleton-color: #070000 !important;
  --el-skeleton-to-color: rgba(248, 169, 0, 1) !important;
}

.skeleton-item {
  border-radius: 13px;
}

.swiper-container {
  position: relative;
  width: 100%;
  max-width: 100%;
  overflow: hidden;
}

.token-swiper {
  width: 100% !important;
  max-width: 100% !important;
  overflow: hidden;
}

:deep(.swiper-wrapper) {
  width: 100% !important;
  max-width: 100% !important;
}

:deep(.swiper-slide) {
  width: 100% !important;
  max-width: 100% !important;
  flex-shrink: 0 !important;
}

.swiper-slide {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 100% !important;
  max-width: 100% !important;
  flex-shrink: 0;
  box-sizing: border-box;
}

.token-slide-content {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 20px;
  background: #070000;
  border-radius: 15px;
  width: 100%;
  height: 62px;
  flex-shrink: 0;
  box-sizing: border-box;
}

.token-image {
  width: 50px;
  height: 50px;
  object-fit: contain;
}

.token-value {
  font-family: Eczar;
  font-weight: 700;
  font-size: 32px;
  line-height: 1;
  color: var(--main-text-color);
}

.swiper-button-prev,
.swiper-button-next {
  position: relative;
  width: 25px;
  height: 60px;
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
  border: none;
  background: none;
  top: unset;
  right: unset;
  left: unset;
  bottom: unset;
  margin: 0;

  &:hover:not(.swiper-button-disabled) {
    filter: drop-shadow(0 4px 8px rgba(248, 169, 0, 0.3));
  }

  &.swiper-button-disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::after {
    display: none;
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: url('~/public/img/icons/chevron_right_orange.svg') no-repeat center center;
    background-size: contain;
  }
}

.swiper-button-prev {
  &::before {
    transform: rotate(180deg);
  }
}

.default-display {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 20px;
  height: 100%;
}

@media screen and (max-width: 479px) {
  .swiper-container {
    padding: 0 40px;
  }

  .token-swiper {
    height: 70px;
  }

  .token-slide-content {
    gap: 15px;
    padding: 15px;
    min-width: 200px;
    height: 50px;
  }

  .token-image {
    width: 40px;
    height: 40px;
  }

  .token-value {
    font-size: 24px;
  }

  .swiper-button-prev,
  .swiper-button-next {
    width: 20px;
    height: 50px;

    &::before {
      width: 20px;
      height: 50px;
    }
  }
}
</style>
